import { get, isObject, isEmpty } from "lodash";
import { FIELDS, START_NODE_NAME } from "./datamodels.constants";
import { EMPTY_STRING, EMPTY_ARRAY, HANDLE_TYPES, DATA_TYPES, ONE } from "../../constants";
import { checkIsConnectionComplete, getStartNode } from "./services.helpers";

const { TRIGGER, VALUE } = HANDLE_TYPES;
const { SELECT_QUERY, EXPRESSION, MODEL_NAME, CUSTOM, CUSTOM_REQUEST } = FIELDS;
const { STRING, OBJECT } = DATA_TYPES
const PARAMS = "params";
const COLUMNS = "columns";
const NOT_FOUND = "notFound";
const BODY_TITLE = "Body";
const QUERY = "Query";
const SINGLE = "single";
const MULTI = "multi";
const EXPRESSION_BODY = "body";
const EXPRESSION_QUERY = "query";
const OBJECT_EXPRESSION_FIELD = ["pickedValueExp", "collection"];
const FOR_EACH_NODE_ID = 51;
const GET_RECORD_VALUE_NODE_ID = 29;
const objectTypeNodes = [FOR_EACH_NODE_ID, GET_RECORD_VALUE_NODE_ID];

export const getFormattedObject = (data) => {
  const formattedData = data.reduce((formattedData, field) => {
    formattedData.push({ name: field, body: EMPTY_ARRAY });
    return formattedData;
  }, []);

  return formattedData;
};

const getProperties = (model, collections) => {
  const collection = collections.find(
    (collection) => collection.refName === model
  );
  const collectionProperties = get(
    collection,
    ["schema", "properties"],
    EMPTY_ARRAY
  );
  const propertiesNames = collectionProperties.map(({ schema }) => schema.key);
  return propertiesNames;
};

export const getCollectionProperties = (data, collections) => {
  if (typeof data === STRING) {
    const propertiesNames = getProperties(data, collections);
    return propertiesNames;
  } else {
    const filteredCollection = [...new Set(data)];
    const formattedData = filteredCollection?.reduce((formattedData, field) => {
      const propertiesNames = getProperties(field, collections);
      formattedData.push({
        name: field,
        body: getFormattedObject(propertiesNames),
      });
      return formattedData;
    }, []);
    return formattedData;
  }
};

const addObjectToParent = (parent, { key, indent }) => {
  const obj = { name: key, body: [] };
  if (indent === ONE) parent.push(obj);
  else {
    const lastItem = parent[parent.length - 1];
    if (lastItem) addObjectToParent(lastItem.body, { key, indent: indent - ONE, });
  }
};

const processInputData = (input, output) => {
  if (Array.isArray(input)) input.forEach((item) => addObjectToParent(output, item));
  else if (typeof input === OBJECT && input !== null) {
    Object.keys(input).forEach((key) => {
      const value = input[key];
      if (Array.isArray(value)) {
        const newItem = { key: key, indent: ONE };
        addObjectToParent(output, newItem);
        value.forEach((item) => {
          processInputData(item, output[output.length - 1].body);
        });
      } else if (isObject(value) && value !== null) {
        const newItem = { key, indent: ONE };
        addObjectToParent(output, newItem);
        processInputData(value, output[output.length - 1].body);
      } else {
        const newItem = { key, indent: ONE };
        addObjectToParent(output, newItem);
      }
    });
  }
};

export const getValidResponseModel = (input) => {
  const output = [];
  processInputData(input, output);
  return output;
};

export const getRequestBodyExpressionFields = (
  { type, models, modelName, customModels, customRequest },
  collections
) => {
  switch (type) {
    case MULTI:
      const data = models.reduce((formattedData, { name, modelName }) => {
        formattedData.push({
          name,
          body: getFormattedObject(
            getCollectionProperties(modelName, collections)
          ),
        });
        return formattedData;
      }, []);
      return data;
    case SINGLE:
      const collection = modelName;
      const properties = getCollectionProperties(collection, collections);
      return getFormattedObject(properties);
    case CUSTOM:
      const customData = customModels.fields.map(({ name }) => name);
      return getFormattedObject(customData);
    case CUSTOM_REQUEST:
      const customRequestData = getValidResponseModel(customRequest);
      return customRequestData;
    default:
      return null;
  }
};

export const getParamProperties = (
  nodes,
  collections,
  isOutputHandleTypeObject,
  isValidationObject
) => {
  const startNode = getStartNode(nodes);
  const startNodeConfig = get(startNode, ["data", "config"], EMPTY_ARRAY);
  const allParams = startNodeConfig.reduce((params, config) => {
    if (config.enable) {
      switch (config.name) {
        case BODY_TITLE:
          const requestBodyExpressionFields = getRequestBodyExpressionFields(
            config.defaultValue,
            collections
          );
          if (!isEmpty(requestBodyExpressionFields))
            params.push({
              name: config.name.toLowerCase(),
              body: requestBodyExpressionFields,
            });
          break;
        case QUERY:
          const allQuery = config.defaultValue.map(({ name }) => name);
          if (isOutputHandleTypeObject && isValidationObject)
            params.push({ name: config.name, body: EMPTY_ARRAY });
          else if (!isEmpty(allQuery))
            params.push({
              name: config.name.toLowerCase(),
              body: getFormattedObject(allQuery),
            });
          break;
        default:
          break;
      }
    }
    return params;
  }, []);
  return allParams;
};

export const getValueTypeProperties = (parentNode, nodes, collections) => {
  const parentExpressionField = parentNode.data.config.find((field) =>
    OBJECT_EXPRESSION_FIELD.includes(field.key)
  );
  const expressionDefaultValue = get(
    parentExpressionField,
    "defaultValue",
    EMPTY_STRING
  );
  if (!expressionDefaultValue?.includes(".")) return;
  const expressionFieldValue = expressionDefaultValue.split(".");
  const startNode = getStartNode(nodes);
  const startNodeConfig = get(startNode, ["data", "config"], EMPTY_ARRAY);

  const expressionValueLastIndex = expressionFieldValue.pop();
  switch (expressionValueLastIndex) {
    case EXPRESSION_BODY:
      const startNodeBody = startNodeConfig.find(
        ({ name }) => name === BODY_TITLE
      );
      const selectedBodyRequestType = get(
        startNodeBody,
        ["defaultValue", "type"],
        EMPTY_STRING
      );
      if (selectedBodyRequestType === SINGLE) {
        const collectionName = get(startNodeBody, ["defaultValue", MODEL_NAME]);
        const bodyProperties = getCollectionProperties(
          collectionName,
          collections
        );
        return bodyProperties;
      }

      if (selectedBodyRequestType === CUSTOM) {
        const bodyProperties =
          startNodeBody.defaultValue.customModels.fields.map(
            ({ name }) => name
          );
        return bodyProperties;
      }
      return;

    case EXPRESSION_QUERY:
      const startNodeParameter = startNodeConfig.find(
        ({ name }) => name === QUERY
      );
      const parameterProperties = startNodeParameter.defaultValue.map(
        ({ name }) => name
      );
      return parameterProperties;

    default:
      const secondLastIndexExpression = expressionFieldValue.pop();
      if (secondLastIndexExpression === BODY_TITLE.toLowerCase()) {
        const startNodeBody = startNodeConfig.find(
          ({ name }) => name === BODY_TITLE
        );
        const isMultiModelSelected =
          get(startNodeBody, ["defaultValue", "type"]) === MULTI;
        if (!isMultiModelSelected) return;
        const multiModelDefaultValue = startNodeBody.defaultValue.models.find(
          ({ name }) => name === expressionValueLastIndex
        );
        const multiModelCollectionName = get(
          multiModelDefaultValue,
          "modelName",
          EMPTY_STRING
        );
        const multiModelCollectionProperties = getCollectionProperties(
          multiModelCollectionName,
          collections
        );
        return multiModelCollectionProperties;
      }
      return;
  }
};

export const checkIsInputHandleTypeValue = (parentNode) =>
  objectTypeNodes.includes(parentNode.data.nodeId);

const handleValidationObject = (
  edges,
  selectedNodeDetails,
  input,
  validationObject,
  nodes,
  collections,
  isOutputHandleTypeObject
) => {
  const selectedNodeConnection = edges.find(
    ({ target, targetHandle }) =>
      target === selectedNodeDetails.id && targetHandle === `tava_${input.key}`
  );
  if (!selectedNodeConnection) {
    validationObject[input.name] = [];
    return;
  }
  const parentNode = nodes.find(
    (node) => node.id === selectedNodeConnection.source
  );
  const isStartNode = get(parentNode, ["data", "name"]) === START_NODE_NAME;
  const isSourceHandleNotFound =
    selectedNodeConnection.source.includes(NOT_FOUND);
  const isConnectedToNotFound =
    isSourceHandleNotFound &&
    checkIsConnectionComplete(parentNode, nodes, edges);
  if (isStartNode || isConnectedToNotFound) {
    validationObject[input.name] = getParamProperties(
      nodes,
      collections,
      isOutputHandleTypeObject,
      true
    );
  } else if (
    !checkIsConnectionComplete(parentNode, nodes, edges) ||
    isOutputHandleTypeObject
  )
    validationObject[input.name] = EMPTY_ARRAY;
  else if (!checkIsInputHandleTypeValue(parentNode)) {
    const collectionField = get(
      parentNode,
      ["data", "config"],
      EMPTY_ARRAY
    ).find(({ key }) => key === MODEL_NAME);
    const collectionName = get(collectionField, "defaultValue", EMPTY_STRING);
    validationObject[input.name] = getCollectionProperties(
      collectionName,
      collections
    );
  } else {
    validationObject[input.name] =
      getValueTypeProperties(parentNode, nodes, collections) || [];
  }
};

const convertValidationObject = (validationObject) => {
  return Object.entries(validationObject).reduce(
    (obj, [key, value]) => ({
      ...obj,
      [key]: value?.reduce((allProperties, properties) => {
        if (isObject(properties)) {
          allProperties[properties.name.toLowerCase()] = properties.body.reduce(
            (subProperties, property) => {
              if (isObject(property)) {
                subProperties[property.modelName] = property.fields.reduce(
                  (totalFields, field) => ({ ...totalFields, [field]: "text" }),
                  {}
                );
              } else subProperties[property] = "text";
              return subProperties;
            },
            {}
          );
        } else allProperties[properties] = "text";
        return allProperties;
      }, {}),
    }),
    {}
  );
};

export const createValidationObject = (
  selectedNodeDetails,
  edges,
  selectedNode,
  nodes,
  collections,
  expressionKey
) => {
  if (!selectedNodeDetails) return;
  const nodeDataFields = [{ name: PARAMS }];
  const modelNameField = selectedNodeDetails.config.find(
    ({ key }) => key === "modelName"
  );
  const modelNameValue = modelNameField?.defaultValue;

  const isOutputHandleTypeObject = selectedNode.outputs.some(
    (output) => output.type === VALUE
  );

  const shouldShowColumnsSection =
    (expressionKey === SELECT_QUERY || expressionKey === EXPRESSION) &&
    modelNameField;
  if (shouldShowColumnsSection) nodeDataFields.push({ name: COLUMNS });

  selectedNodeDetails.inputs.forEach(
    (input) => input.type !== TRIGGER && nodeDataFields.push(input)
  );

  const validationObject = nodeDataFields.reduce(
    (obj, { name }) => ({ ...obj, [name]: [] }),
    {}
  );

  nodeDataFields.forEach((input) => {
    switch (input.name) {
      case PARAMS:
        validationObject.params = getParamProperties(
          nodes,
          collections,
          isOutputHandleTypeObject,
          true
        );
        break;
      case COLUMNS:
        validationObject.columns =
          modelNameValue &&
          getCollectionProperties(modelNameValue, collections);
        break;
      default:
        handleValidationObject(
          edges,
          selectedNodeDetails,
          input,
          validationObject,
          nodes,
          collections,
          isOutputHandleTypeObject
        );
    }
  });
  return convertValidationObject(validationObject);
};
